import api from '../../../app/api';
import { PaginatedList } from '../../../shared/models/paginated-list';
import { TraceLogs } from '../../../shared/models/trace-logs';
import { transformTraceLogsResponse } from '../../../shared/utils/transform-trace-logs-response';
import { ScanRequest } from '../models/scan-request';
import { ScanRequestAttachParams } from '../models/scan-request-attach-params';
import { ScanRequestAttachResponse } from '../models/scan-request-attach-response';
import { ScanRequestCreate } from '../models/scan-request-create';
import { ScanRequestParams } from '../models/scan-request-params';

export const scanRequestsApi = api
  .enhanceEndpoints({
    addTagTypes: ['Request', 'ScanRequests'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getScanRequests: builder.query<PaginatedList<ScanRequest>, ScanRequestParams>({
        query: params => ({
          url: '/portal-api/v1/scan-requests',
          params,
        }),
        providesTags: ['ScanRequests'],
      }),
      getScanRequestsTraceLogs: builder.query<PaginatedList<TraceLogs>, { id: string; page: number }>({
        query: ({ id, ...params }) => ({
          url: `/portal-api/v1/scan-requests/${id}/trace-logs`,
          params,
        }),
        providesTags: ['ScanRequests'],
        transformResponse: transformTraceLogsResponse,
      }),
      attachScanRequest: builder.mutation<ScanRequestAttachResponse, ScanRequestAttachParams>({
        query: ({ scanRequestId, ...body }) => ({
          url: `/portal-api/v1/scan-requests/${scanRequestId}/attach`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['Request', 'ScanRequests'],
      }),
      detachScanRequest: builder.mutation<ScanRequest, string>({
        query: id => ({
          url: `/portal-api/v1/scan-requests/${id}/detach`,
          method: 'POST',
        }),
        invalidatesTags: ['Request', 'ScanRequests'],
      }),
      deleteScanRequest: builder.mutation<ScanRequest, string>({
        query: id => ({
          url: `/portal-api/v1/scan-requests/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Request', 'ScanRequests'],
      }),
      restoreScanRequest: builder.mutation<ScanRequest, string>({
        query: id => ({
          url: `/portal-api/v1/scan-requests/${id}/restore`,
          method: 'POST',
        }),
        invalidatesTags: ['Request', 'ScanRequests'],
      }),
      createScanRequest: builder.mutation<ScanRequest, ScanRequestCreate>({
        query: body => ({
          url: '/portal-api/v1/scan-requests',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['Request', 'ScanRequests'],
      }),
      updateScanRequest: builder.mutation<ScanRequest, Pick<ScanRequest, 'name' | 'id'>>({
        query: body => ({
          url: `/portal-api/v1/scan-requests/${body.id}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['Request', 'ScanRequests'],
      }),
      archiveScanRequest: builder.mutation<void, { scanRequests: string[] }>({
        query: body => ({
          url: '/portal-api/v1/scan-requests/archive',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['ScanRequests', 'Request'],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetScanRequestsQuery,
  useAttachScanRequestMutation,
  useGetScanRequestsTraceLogsQuery,
  useDetachScanRequestMutation,
  useDeleteScanRequestMutation,
  useRestoreScanRequestMutation,
  useUpdateScanRequestMutation,
  useCreateScanRequestMutation,
  useArchiveScanRequestMutation,
} = scanRequestsApi;
